import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { APP_NAME } from "../config/branding";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [userType, setUserType] = useState("user");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    const typeParam = searchParams.get("type");
    
    if (!tokenParam) {
      toast.error("Invalid reset link");
      navigate("/login");
    } else {
      setToken(tokenParam);
      // Only allow "user" or "admin" types
      if (typeParam === "admin" || typeParam === "user") {
        setUserType(typeParam);
      }
    }
  }, [searchParams, navigate]);

  // Password validation
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 12) {
      errors.push("Must be at least 12 characters");
    }
    
    const checks = [
      /[A-Z]/.test(password),  // uppercase
      /[a-z]/.test(password),  // lowercase
      /\d/.test(password),      // digit
      /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password)  // symbol
    ];
    
    if (checks.filter(Boolean).length < 3) {
      errors.push("Must contain 3 of 4: uppercase, lowercase, number, symbol");
    }
    
    return errors;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordErrors(validatePassword(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/password-reset/confirm', {
        token: token,
        user_type: userType,
        new_password: newPassword
      });

      if (response.data.ok) {
        toast.success("Password reset successful");
        setSuccess(true);

        // Redirect to appropriate login after 2 seconds
        setTimeout(() => {
          navigate(userType === "admin" ? "/admin/login" : "/login");
        }, 2000);
      } else {
        toast.error(response.data.error || "Failed to reset password");
      }

    } catch (error) {
      console.error("Reset password error:", error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          "Failed to reset password. The link may be invalid or expired.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-heading text-3xl font-bold text-foreground">
                {APP_NAME}
              </h1>
            </Link>
          </div>

          <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-950 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              
              <h2 className="text-2xl font-semibold mb-3">Password Reset Complete</h2>
              
              <p className="text-muted-foreground mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>

              <p className="text-sm text-muted-foreground">
                Redirecting to {userType === "admin" ? "admin " : ""}login page...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-3xl font-bold text-foreground">
              {APP_NAME}
            </h1>
          </Link>
        </div>

        <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Reset Your Password</h2>
            <p className="text-muted-foreground">
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium mb-2">
                New Password
              </label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={handlePasswordChange}
                  disabled={loading}
                  required
                  minLength={12}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {passwordErrors.length > 0 && newPassword.length > 0 && (
                <div className="mt-2 space-y-1">
                  {passwordErrors.map((error, idx) => (
                    <p key={idx} className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {error}
                    </p>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Min 12 characters with 3 of 4: uppercase, lowercase, number, symbol
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                Confirm Password
              </label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !token || passwordErrors.length > 0}
              data-testid="reset-password-submit"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
